import { useEffect, useState } from 'react';
import MainPageLayout from 'layouts/MainPageLayout';
import { FormattedMessage } from 'react-intl';
import useStyles from './PartnerPublications.styles';
import { useSelectedLinkContext } from 'contexts';
import { useQuery } from '@apollo/client';
import {
  GET_PARTNER_ACCOUNTS_WITH_FAMILY_PORTAL_QUERY,
  GetPartnerAccountsWithFamilyPortalOutput,
} from 'graphql/partnerdocuments/PartnerDocumentsQueries';
import { navigateToSelectedChannelRoute } from 'utils/history';
import Route from 'routes/Routes';
import { useParams } from 'react-router';
import { PartnerParams } from 'types/RouteParams';
import { PartnerAccount } from 'types';
import PartnerBookmarkList from './PartnerPublications/PartnerBookmarkList';
import PartnerContentList from './PartnerPublications/PartnerContentList';
import PartnerDocumentList from './PartnerPublications/PartnerDocumentList';
import { PartnerPublicationsContextProvider } from './PartnerPublications/PartnerPublicationsContext';
import { PartnerContent } from 'types/Partner';
import PartnerContentView from './PartnerPublications/PartnerContent/PartnerContentView';
import { PartnerContentContextProvider } from './PartnerPublications/PartnerContent/PartnerContentContext';

type PartnerPublicationsPureProps = {
  partnerAccount: PartnerAccount;
};

const PartnerPublicationsPure = ({
  partnerAccount,
}: PartnerPublicationsPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <MainPageLayout title={partnerAccount.partnerProfile.name}>

      <div className={classes.description}>
        <FormattedMessage id="partner.publications.description" />
      </div>

      <div className={classes.contentsContainer}>
        <div className={classes.leftContainer}>
          <PartnerDocumentList />
        </div>

        <div className={classes.rightContainer}>
          <PartnerContentList />
        </div>

        <div className={classes.leftContainer}>
          <PartnerBookmarkList />
        </div>
      </div>
    </MainPageLayout>
  );
};

const PartnerPublications = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const { partnerId } = useParams<PartnerParams>();

  const [contents, setContents] = useState<PartnerContent[]>([]);
  const [selectedContent, setSelectedContent] = useState<
    PartnerContent | undefined
  >();

  const { data, loading } =
    useQuery<GetPartnerAccountsWithFamilyPortalOutput>(
      GET_PARTNER_ACCOUNTS_WITH_FAMILY_PORTAL_QUERY,
      {
        variables: {
          channel: selectedLink?.channelKey,
        },
      },
    );

  const partnerAccounts =
    data?.getPartnerAccountsWithFamilyPortal?.partnerAccounts || [];

  const partnerAccount = partnerAccounts.find((_) => _.id === partnerId);

  useEffect(() => {
    if (!loading && !partnerAccount) {
      navigateToSelectedChannelRoute(selectedLink, Route.HELP);
    }
  }, [loading, partnerAccount, selectedLink]);

  if (loading || !partnerAccount) {
    return null;
  } else if (selectedContent) {
    return (
      <PartnerContentContextProvider
        value={{
          contents,
          partnerAccount,
          selectedContent,
          setSelectedContent,
        }}
      >
        <PartnerContentView />
      </PartnerContentContextProvider>
    );
  }

  return (
    <PartnerPublicationsContextProvider
      value={{
        contents,
        partnerAccount,
        setContents,
        setSelectedContent,
      }}
    >
      <PartnerPublicationsPure partnerAccount={partnerAccount} />
    </PartnerPublicationsContextProvider>
  );
};

export default PartnerPublications;
